import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, mediaUrl, asArray } from "@/lib/api";
import MediaUploader, { ImageField } from "@/components/MediaUploader";
import { useEscapeKey } from "@/lib/useEscapeKey";
import { toast } from "@/lib/toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Building2, MessageSquare, Wallet, TrendingUp, Check, X,
  Sparkles, Trash2, LayoutGrid, Send, MessageCircle, Phone, Mail,
  Plus, Bell, Settings2, FileText, Star, Kanban, ShieldCheck, Search, Pencil, Home as HomeIcon, ArrowUp, ArrowDown, Newspaper, CalendarClock, Copy
} from "lucide-react";
import VenueForm from "@/components/VenueForm";
import CitySelect from "@/components/CitySelect";
import { CAT_LABEL } from "@/components/VenueCard";
import DashboardShell, { Tile, Panel } from "@/components/DashboardShell";
import Insights from "@/components/admin/Insights";
import { useAuth } from "@/context/AuthContext";
import { useTick } from "@/context/LiveContext";
import { useSearchParams } from "react-router-dom";
import { CATEGORIES, catLabel } from "@/lib/categories";
import { BarChart3, Inbox, Globe, Youtube as YoutubeIcon, Banknote, Activity, Receipt } from "lucide-react";
import OfflinePayments, { PaymentForm } from "@/components/admin/OfflinePayments";
import SystemStatus, { useBackendHealth, OutdatedBanner } from "@/components/admin/SystemStatus";
import { phoneError, emailError, cleanPhoneInput, collect } from "@/lib/validate";
import { withDefaults } from "@/lib/pricing";

const STAGES = [
  { key: "new", label: "New", color: "bg-[#FCFBD4] text-[#5A570C] border-[#E9E5A0]" },
  { key: "contacted", label: "Contacted", color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { key: "shortlisted", label: "Shortlisted", color: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "negotiating", label: "Negotiating", color: "bg-violet-50 text-violet-800 border-violet-200" },
  { key: "booked", label: "Booked ✨", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "lost", label: "Lost", color: "bg-rose-50 text-rose-800 border-rose-200" },
];

const BLOG_TOPICS = ["Venues", "Catering", "Decor", "Photography", "Anchors", "Music", "Planning", "Budget", "Trends"];

const PERMISSION_KEYS = [
  { k: "venues", l: "Manage venues (approve/highlight/delete)" },
  { k: "users", l: "View & manage users" },
  { k: "cities", l: "Add/remove cities" },
  { k: "pricing", l: "Edit pricing" },
  { k: "crm", l: "Access CRM leads" },
  { k: "cms", l: "Content: blogs & testimonials" },
  { k: "reminders", l: "Send renewal reminders" },
  { k: "reports", l: "View revenue & analytics" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const tick = useTick();
  const [sp, setSp] = useSearchParams();
  const role = user?.role;
  const full = role === "super_admin" || role === "major_admin";
  const [tab, setTabState] = useState(sp.get("tab") || (full ? "insights" : "crm"));
  const [stats, setStats] = useState({});
  const [venues, setVenues] = useState([]);
  const [cities, setCities] = useState([]);
  const [pricing, setPricing] = useState({});
  const [msgCount, setMsgCount] = useState(0);
  const [addForm, setAddForm] = useState(false);
  const { health, outdated } = useBackendHealth();
  const setTab = (k) => { setTabState(k); const n = new URLSearchParams(sp); n.set("tab", k); setSp(n, { replace: true }); };

  const load = () => {
    if (full) api.get("/stats/overview").then(r=>setStats(r.data || {})).catch(()=>{});
    // /admin/venues includes listings waiting for approval; older servers only have the public list
    api.get("/admin/venues").then(r=>setVenues(asArray(r.data))).catch((e)=>{
      if ([404, 405].includes(e?.response?.status)) api.get("/venues", { params: { limit: 2000 } }).then(r=>setVenues(asArray(r.data))).catch(()=>{});
    });
    api.get("/config/pricing").then(r=>setPricing(withDefaults(r.data))).catch(()=>setPricing(withDefaults({})));
    api.get("/contact-messages").then(r=>setMsgCount(asArray(r.data).filter((m) => !m.read).length)).catch(()=>{});
  };
  const loadCities = () => api.get("/cities").then(r=>setCities(asArray(r.data))).catch(()=>{});
  useEffect(()=>{ load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ if (tab === "cities" && !cities.length) loadCities(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = venues.filter((v) => !v.is_approved).length;
  const ALL = [
    { key: "insights", label: "Statistics", Icon: BarChart3, group: "Overview", full: true },
    { key: "overview", label: "Today", Icon: LayoutGrid, group: "Overview", badge: pending || null },
    { key: "crm", label: "CRM · Leads", Icon: Kanban, group: "Sales" },
    { key: "renewals", label: "Renewals", Icon: CalendarClock, group: "Sales" },
    { key: "payments", label: "Cash · UPI · Cheque", Icon: Banknote, group: "Sales", full: true },
    { key: "venues", label: "Listings", Icon: Building2, group: "Marketplace", full: true, badge: pending || null },
    { key: "users", label: "Users", Icon: Users, group: "Marketplace", full: true },
    { key: "cities", label: "Cities", Icon: Star, group: "Marketplace", full: true },
    { key: "pricing", label: "Pricing", Icon: Wallet, group: "Marketplace", full: true },
    { key: "cms", label: "CMS", Icon: FileText, group: "Content", badge: msgCount || null },
    { key: "homepage", label: "Homepage", Icon: HomeIcon, group: "Content", full: true },
    { key: "newsletter", label: "Newsletter", Icon: Newspaper, group: "Content", full: true },
    { key: "team", label: "Team & roles", Icon: ShieldCheck, group: "Settings", full: true },
    { key: "reminders", label: "Reminder emails", Icon: Bell, group: "Settings", full: true },
    { key: "system", label: "System status", Icon: Activity, group: "Settings", full: true, badge: outdated ? "!" : null },
  ];
  const items = ALL.filter((i) => full || !i.full);
  const current = items.find((i) => i.key === tab) ? tab : items[0].key;

  return (
    <DashboardShell title={full ? "Control centre" : "Sales desk"} subtitle={full ? "Listings, leads, users, content and the numbers behind DartMyVenue." : "Your leads, renewals and content."} items={items} active={current} onSelect={setTab}
      actions={full ? <button onClick={()=>setAddForm(true)} className="btn-wine" data-testid="admin-header-add-venue"><Plus className="w-4 h-4"/> Add listing</button> : null}>
      {full && outdated && current !== "system" && <OutdatedBanner health={health} onOpen={()=>setTab("system")}/>}
      {!["insights", "payments", "system"].includes(current) && full && (
        <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-6">
          <Tile icon={Users} label="Users" value={Number(stats.users||0).toLocaleString("en-IN")}/>
          <Tile icon={Building2} label="Listings" value={Number(stats.venues||0).toLocaleString("en-IN")}/>
          <Tile icon={MessageSquare} label="Inquiries" value={Number(stats.inquiries||0).toLocaleString("en-IN")}/>
          <Tile icon={Wallet} label="Paid plans" value={stats.paid_subs ?? 0}/>
          <Tile icon={TrendingUp} label="Revenue" value={`₹${Number(stats.revenue||0).toLocaleString("en-IN")}`}/>
          <Tile icon={Sparkles} label="Visits" value={Number(stats.visitors||0).toLocaleString("en-IN")}/>
        </div>
      )}
      {current==="insights" && <Insights/>}
      {current==="overview" && <OverviewTab venues={venues} onReload={load}/>}
      {current==="crm" && <CRMTab/>}
      {current==="venues" && <VenuesTab venues={venues} onReload={load} full={full}/>}
      {current==="payments" && <OfflinePayments venues={venues}/>}
      {current==="system" && <SystemStatus/>}
      {addForm && <VenueForm initial={null} onClose={()=>setAddForm(false)} onSaved={()=>{ setAddForm(false); load(); setTab("venues"); }}/>}
      {current==="users" && <UsersTab/>}
      {current==="team" && <TeamTab/>}
      {current==="cms" && <CMSTab onChange={load}/>}
      {current==="renewals" && <RenewalsTab/>}
      {current==="homepage" && <HomepageTab/>}
      {current==="newsletter" && <NewsletterTab/>}
      {current==="cities" && <CitiesTab cities={cities} onReload={loadCities}/>}
      {current==="pricing" && <PricingTab pricing={pricing} setPricing={setPricing}/>}
      {current==="reminders" && <RemindersTab/>}
    </DashboardShell>
  );
}

/* ============ Overview ============ */
function OverviewTab({ venues, onReload }) {
  const [leads, setLeads] = useState([]);
  useEffect(()=>{ api.get("/leads").then(r=>setLeads(asArray(r.data))).catch(()=>{}); }, []);
  const approve = async (id) => { await api.post(`/venues/${id}/approve`); toast.success("Approved"); onReload(); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card title="Pending Approvals">
        <div className="space-y-1">
          {venues.filter(v=>!v.is_approved).slice(0,10).map(v=>(
            <div key={v.id} className="flex items-center justify-between border-b border-[color:var(--line)] py-2.5 text-sm">
              <div>
                <div className="font-medium">{v.name}</div>
                <div className="text-xs text-neutral-500">{v.city} · {catLabel(v.category)}</div>
              </div>
              <button onClick={()=>approve(v.id)} className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Approve</button>
            </div>
          ))}
          {!venues.filter(v=>!v.is_approved).length && <div className="text-neutral-500 text-sm py-6">Nothing pending — all clear ✨</div>}
        </div>
      </Card>
      <Card title="Fresh Leads">
        <div className="space-y-1">
          {leads.slice(0,6).map(l=>(
            <div key={l.id} className="flex items-center justify-between border-b border-[color:var(--line)] py-2.5 text-sm">
              <div>
                <div className="font-medium">{l.name}</div>
                <div className="text-xs text-neutral-500">{l.phone} · {l.event_date || "—"} · {l.pax || "?"} pax</div>
              </div>
              <StageBadge stage={l.stage}/>
            </div>
          ))}
          {!leads.length && <div className="text-neutral-500 text-sm py-6">No leads yet. Public inquiries auto-generate leads.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============ CRM ============ */
function CRMTab() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);

  const load = () => api.get("/leads", { params: { q }}).then(r=>setLeads(asArray(r.data))).catch(()=>{});
  useEffect(()=>{ const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = STAGES.reduce((acc, s) => { acc[s.key] = leads.filter(l => l.stage === s.key); return acc; }, {});

  const move = async (id, stage) => {
    await api.patch(`/leads/${id}`, { stage });
    toast.success(`Moved to ${stage}`);
    load();
  };

  const importInquiries = async () => {
    const { data } = await api.post("/leads/import-inquiries");
    toast.success(`Imported ${data.imported} lead(s)`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search leads by name, phone or email" className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[color:var(--line)] bg-white text-sm" data-testid="crm-search"/>
        </div>
        <button onClick={importInquiries} className="btn-ghost !py-2 !px-4 text-sm" data-testid="crm-import-inquiries"><MessageSquare className="w-4 h-4"/> Import inquiries</button>
        <button onClick={()=>setOpenAdd(true)} className="btn-wine !py-2 !px-4 text-sm" data-testid="crm-add-lead"><Plus className="w-4 h-4"/> New lead</button>
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(232px,1fr)] gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x" data-testid="crm-kanban">
        {STAGES.map(s => (
          <div key={s.key} className={`rounded-xl border ${s.color} p-3 min-h-[400px] snap-start`} data-testid={`crm-col-${s.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest-2 font-medium">{s.label}</div>
              <span className="text-[10px] px-1.5 rounded-full bg-white/70">{groups[s.key]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {groups[s.key]?.map(l => (
                <motion.button
                  key={l.id}
                  layout
                  onClick={()=>setSelected(l)}
                  className="w-full text-left p-3 rounded-lg bg-white border border-[color:var(--line)] hover:shadow-lg hover:border-[color:var(--wine)]/40 transition-all cursor-pointer group"
                  data-testid={`crm-card-${l.id}`}
                >
                  <div className="font-medium text-sm text-neutral-900 flex items-center justify-between">
                    <span className="truncate">{l.name}</span>
                    {l.source === "inquiry" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FCFBD4] text-[#5A570C] shrink-0">web</span>}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-2">
                    <Phone className="w-3 h-3"/> {l.phone}
                  </div>
                  {(l.event_date || l.pax) && (
                    <div className="text-[11px] text-neutral-500 mt-1">
                      {l.event_date && <span>{l.event_date}</span>}{l.event_date && l.pax && " · "}{l.pax && <span>{l.pax} pax</span>}
                    </div>
                  )}
                </motion.button>
              ))}
              {!groups[s.key]?.length && <div className="text-[11px] text-neutral-500 text-center py-6">Nothing here</div>}
            </div>
          </div>
        ))}
      </div>

      {selected && <LeadDetail lead={selected} onClose={()=>{ setSelected(null); load(); }} onMove={move}/>}
      {openAdd && <AddLeadModal onClose={()=>setOpenAdd(false)} onSaved={()=>{ setOpenAdd(false); load(); }}/>}
    </div>
  );
}

function StageBadge({ stage }) {
  const s = STAGES.find(x=>x.key===stage) || STAGES[0];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>;
}

function LeadDetail({ lead: initial, onClose, onMove }) {
  const [lead, setLead] = useState(initial);
  const [note, setNote] = useState("");
  const [team, setTeam] = useState([]);
  const [waMsg, setWaMsg] = useState("Hi, this is from DartMyVenue regarding your wedding venue inquiry. Do you have a moment to talk?");
  useEscapeKey(onClose);

  useEffect(()=>{ api.get("/team/admins").then(r=>setTeam(r.data)).catch(()=>{}); }, []);
  const reload = () => api.get(`/leads/${lead.id}`).then(r=>setLead(r.data));

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${lead.id}/note`, { note });
    toast.success("Note added");
    setNote("");
    reload();
  };
  const changeStage = async (s) => { await onMove(lead.id, s); reload(); };
  const assign = async (uid) => {
    await api.patch(`/leads/${lead.id}`, { assigned_to: uid });
    toast.success("Assigned");
    reload();
  };
  const sendWA = async () => {
    const { data } = await api.post(`/leads/${lead.id}/whatsapp`, { note: waMsg });
    window.open(data.wa_link, "_blank");
    toast.success("Logged & opening WhatsApp");
    reload();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-3 md:p-6" onClick={onClose}>
      <motion.div initial={{y:40, opacity:0}} animate={{y:0, opacity:1}} onClick={e=>e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="lead-detail">
        <div className="p-6 border-b border-[color:var(--line)] flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Lead</div>
            <h2 className="font-display text-3xl">{lead.name}</h2>
            <div className="text-sm text-neutral-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5"/> {lead.phone}</span>
              {lead.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5"/> {lead.email}</span>}
              {lead.event_date && <span>Event: {lead.event_date}</span>}
              {lead.pax && <span>Pax: {lead.pax}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-6 grid md:grid-cols-3 gap-6">
          {/* Left: pipeline + actions */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-2">Move to stage</div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map(s => (
                  <button key={s.key} onClick={()=>changeStage(s.key)} className={`text-xs px-3 py-1.5 rounded-full border transition-all ${lead.stage===s.key ? "bg-[color:var(--wine)] text-[color:var(--gold-2)] border-[color:var(--wine)]" : "bg-white text-neutral-700 border-[color:var(--line)] hover:border-[color:var(--wine)]"}`} data-testid={`stage-${s.key}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="card-luxury p-4">
              <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-2">WhatsApp the lead</div>
              <textarea value={waMsg} onChange={e=>setWaMsg(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="wa-message"/>
              <button onClick={sendWA} className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700" data-testid="wa-send">
                <MessageCircle className="w-4 h-4"/> Send via WhatsApp
              </button>
            </div>

            <div className="card-luxury p-4">
              <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-2">Add note</div>
              <div className="flex gap-2">
                <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Called back at 3pm, sounded keen…" className="flex-1 px-3 py-2 rounded-lg border text-sm" data-testid="note-input"/>
                <button onClick={addNote} className="btn-wine !py-2 !px-4 text-sm" data-testid="note-add">Save</button>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-3">Timeline</div>
              <div className="space-y-3">
                {(lead.timeline || []).slice().reverse().map((t, i)=>(
                  <div key={i} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-[color:var(--wine)] mt-1.5 shrink-0"/>
                    <div className="flex-1">
                      <div className="text-sm">
                        <span className="font-medium capitalize">{(t.event||"").replaceAll("_"," ")}</span>
                        {t.note && <span className="text-neutral-700"> — {t.note}</span>}
                        {t.from && <span className="text-neutral-500 text-xs"> ({t.from} → {t.to})</span>}
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">{new Date(t.at).toLocaleString()}{t.by ? ` · ${t.by}` : ""}</div>
                    </div>
                  </div>
                ))}
                {!(lead.timeline||[]).length && <div className="text-sm text-neutral-500">No activity yet.</div>}
              </div>
            </div>
          </div>

          {/* Right: sidebar */}
          <div className="space-y-4">
            <div className="card-luxury p-4">
              <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-2">Assign to</div>
              <select value={lead.assigned_to || ""} onChange={e=>assign(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="assign-select">
                <option value="">— Unassigned —</option>
                {team.map(u=><option key={u.id} value={u.id}>{u.name} · {u.role.replace("_"," ")}</option>)}
              </select>
            </div>
            <div className="card-luxury p-4 text-sm space-y-2">
              <Row k="Stage" v={<StageBadge stage={lead.stage}/>}/>
              <Row k="Source" v={lead.source || "manual"}/>
              <Row k="Looking for" v={(lead.interests || []).length ? lead.interests.map((k) => catLabel(k)).join(", ") : "—"}/>
              <Row k="Venue" v={lead.venue_id ? <a className="text-[color:var(--wine)] underline" href={`/property/${lead.venue_id}`} target="_blank" rel="noreferrer">Open</a> : "—"}/>
              <Row k="Budget" v={lead.budget ? `₹${Number(lead.budget).toLocaleString("en-IN")}` : "—"}/>
              <Row k="Created" v={new Date(lead.created_at).toLocaleDateString()}/>
            </div>
            {lead.phone && (
              <div className="grid grid-cols-2 gap-2">
                <a href={`tel:${lead.phone}`} className="btn-ghost !py-2 !px-3 text-xs justify-center"><Phone className="w-4 h-4"/> Call</a>
                {lead.email && <a href={`mailto:${lead.email}`} className="btn-ghost !py-2 !px-3 text-xs justify-center"><Mail className="w-4 h-4"/> Email</a>}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ k, v }) { return <div className="flex justify-between"><span className="text-neutral-500">{k}</span><span className="font-medium">{v}</span></div>; }

function AddLeadModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name:"", phone:"", email:"", city:"", event_type:"marriage", event_date:"", pax:"", budget:"", stage:"new", notes:"", interests:["venue"] });
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  useEscapeKey(onClose);
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e })); setErrs((x) => ({ ...x, [k]: "" })); };
  const toggle = (k) => setF((x) => ({ ...x, interests: x.interests.includes(k) ? x.interests.filter((i) => i !== k) : [...x.interests, k] }));
  const save = async (e) => {
    e.preventDefault();
    const found = collect({ name: f.name.trim() ? "" : "Enter a name", phone: phoneError(f.phone, { required: true }), email: emailError(f.email) });
    setErrs(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await api.post("/leads", { ...f, email: f.email || null, event_date: f.event_date || null, pax: f.pax ? Number(f.pax) : undefined, budget: f.budget ? Number(f.budget) : undefined });
      toast.success("Lead created");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const E = ({ k }) => (errs[k] ? <span className="field-error">{errs[k]}</span> : null);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <form noValidate onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 w-full max-w-lg space-y-3 max-h-[94vh] overflow-y-auto" data-testid="add-lead-modal">
        <h2 className="font-display text-2xl text-[color:var(--maroon)]">Create new lead</h2>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="col-span-2"><input placeholder="Name" value={f.name} onChange={set("name")} className={`input ${errs.name ? "invalid" : ""}`} data-testid="l-name"/><E k="name"/></div>
          <div className="col-span-2 sm:col-span-1"><input type="tel" inputMode="tel" placeholder="Mobile" value={f.phone} onChange={(e)=>set("phone")(cleanPhoneInput(e.target.value))} className={`input ${errs.phone ? "invalid" : ""}`} data-testid="l-phone"/><E k="phone"/></div>
          <div className="col-span-2 sm:col-span-1"><input type="email" inputMode="email" placeholder="Email (optional)" value={f.email} onChange={set("email")} className={`input ${errs.email ? "invalid" : ""}`} data-testid="l-email"/><E k="email"/></div>
          <div className="col-span-2 sm:col-span-1"><CitySelect value={f.city} onChange={(c)=>setF((x)=>({ ...x, city: c }))} variant="field" placeholder="City" testId="l-city"/></div>
          <select value={f.event_type} onChange={set("event_type")} className="input col-span-2 sm:col-span-1">
            <option value="marriage">Marriage</option><option value="reception">Reception</option><option value="engagement">Engagement</option><option value="sangeet">Sangeet / Haldi</option><option value="anniversary">Anniversary</option><option value="birthday">Birthday</option><option value="corporate">Corporate</option><option value="other">Other</option>
          </select>
          <input type="date" value={f.event_date} onChange={set("event_date")} className="input" aria-label="Event date"/>
          <input type="number" inputMode="numeric" placeholder="Guests" value={f.pax} onChange={set("pax")} className="input"/>
          <input type="number" inputMode="numeric" placeholder="Budget (₹)" value={f.budget} onChange={set("budget")} className="input col-span-2"/>
          <div className="col-span-2">
            <div className="label">Looking for</div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c)=>(
                <button type="button" key={c.key} onClick={()=>toggle(c.key)} className={`press text-[13px] px-3 py-1.5 rounded-full border ${f.interests.includes(c.key) ? "bg-[color:var(--maroon)] text-white border-[color:var(--maroon)]" : "bg-white border-[color:var(--line)] text-[color:var(--maroon)]"}`} data-testid={`l-interest-${c.key}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <textarea placeholder="Notes" rows={3} value={f.notes} onChange={set("notes")} className="input col-span-2"/>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button disabled={busy} className="btn-wine" data-testid="l-save">{busy ? "Saving…" : "Create lead"}</button>
        </div>
      </form>
    </div>
  );
}

/* ============ Venues ============ */
function VenuesTab({ venues, onReload, full }) {
  const [form, setForm] = useState(null);
  const [pay, setPay] = useState(null);
  const [q, setQ] = useState(""), [cat, setCat] = useState(""), [status, setStatus] = useState(""), [city, setCity] = useState("");
  const approve = async (id) => { await api.post(`/venues/${id}/approve`); toast.success("Approved"); onReload(); };
  const highlight = async (id) => { await api.post(`/venues/${id}/highlight`); toast.success("Premium toggled"); onReload(); };
  const del = async (v) => { if (!window.confirm(`Remove "${v.name}" permanently?`)) return; await api.delete(`/venues/${v.id}`); toast.success("Removed"); onReload(); };
  const ql = q.toLowerCase();
  const rows = venues.filter(v => (!q || String(v.name||"").toLowerCase().includes(ql) || String(v.city||"").toLowerCase().includes(ql) || String(v.owner_name||"").toLowerCase().includes(ql)) && (!city || v.city===city) && (!cat || v.category===cat) && (!status || (status==="pending" ? !v.is_approved : status==="premium" ? v.highlighted : v.is_approved)));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/><input placeholder="Search name, city or owner" value={q} onChange={e=>setQ(e.target.value)} className="input !pl-9 !rounded-full !py-2" data-testid="admin-venue-search"/></div>
        <div className="w-full sm:w-52"><CitySelect value={city} onChange={setCity} allowAll placeholder="All cities" testId="admin-venue-city"/></div>
        <select value={cat} onChange={e=>setCat(e.target.value)} className="input !rounded-full !py-2 !w-auto"><option value="">All categories</option>{CATEGORIES.map((c)=><option key={c.key} value={c.key}>{c.label}</option>)}</select>
        <select value={status} onChange={e=>setStatus(e.target.value)} className="input !rounded-full !py-2 !w-auto"><option value="">Any status</option><option value="live">Live</option><option value="pending">Pending</option><option value="premium">Premium</option></select>
        <button onClick={()=>setForm("new")} className="btn-wine !py-2 !px-4 text-sm" data-testid="admin-add-venue"><Plus className="w-4 h-4"/> {city ? `Add listing in ${city}` : "Add listing"}</button>
      </div>
      <div className="card-luxury p-2 md:p-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-xs uppercase tracking-widest-2 text-neutral-500 text-left border-b border-[color:var(--line)]">
            <tr><th className="p-2">Listing</th><th>City</th><th>Category</th><th>Owner</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(v=>(
              <tr key={v.id} className="border-b border-[color:var(--line)]/60 hover:bg-[color:var(--ivory)]/60" data-testid={`admin-venue-row-${v.id}`}>
                <td className="p-2"><div className="flex items-center gap-3"><img src={mediaUrl(v.images?.[0]) || "/logo-mark.png"} alt="" className="w-12 h-9 rounded-lg object-cover bg-[color:var(--ivory)]"/><div><div className="font-semibold">{v.name}</div><div className="text-[11px] text-neutral-500">{v.views||0} views · {v.pax_min}–{v.pax_max} pax</div></div></div></td>
                <td>{v.city}</td>
                <td className="text-neutral-600">{catLabel(v.category)}</td>
                <td className="text-xs text-neutral-600">{v.owner_name ? <><span className="font-semibold text-[color:var(--ink)]">{v.owner_name}</span><br/></> : null}{v.contact_phone || v.owner_phone || "—"}<br/>{v.contact_email || v.owner_email || ""}</td>
                <td><div className="flex gap-1 flex-wrap">{v.is_approved ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Live</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Pending</span>}{v.highlighted && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--gold)] text-[color:var(--maroon-2)] font-semibold">Premium</span>}</div></td>
                <td>
                  <div className="flex gap-1.5">
                    {!v.is_approved && <button onClick={()=>approve(v.id)} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid={`approve-${v.id}`}>Approve</button>}
                    <button onClick={()=>highlight(v.id)} className="text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100" title="Toggle premium"><Sparkles className="w-3 h-3"/></button>
                    <button onClick={()=>setForm(v)} className="text-[11px] px-2 py-1 rounded-full bg-[color:var(--ivory-2)] text-[color:var(--maroon)] hover:bg-[color:var(--maroon)] hover:text-white" title="Edit" data-testid={`admin-edit-${v.id}`}><Pencil className="w-3 h-3"/></button>
                    {full && <button onClick={()=>setPay(v)} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white" title="Record cash / UPI / cheque payment" data-testid={`admin-pay-${v.id}`}><Receipt className="w-3 h-3"/></button>}
                    <button onClick={()=>del(v)} className="text-[11px] px-2 py-1 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white" title="Remove" data-testid={`admin-delete-${v.id}`}><Trash2 className="w-3 h-3"/></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">No listings match.</td></tr>}
          </tbody>
        </table>
      </div>
      {form && <VenueForm initial={form==="new" ? null : form} defaultCity={city} onClose={()=>setForm(null)} onSaved={()=>{ setForm(null); onReload(); }}/>}
      {pay && <PaymentForm prefill={{ venue: pay }} venues={venues} onClose={()=>setPay(null)} onSaved={()=>{ setPay(null); onReload(); toast.success("Saved in Cash · UPI · Cheque"); }}/>}
    </div>
  );
}

/* ============ Renewals board ============ */
function RenewalsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(()=>{ api.get("/admin/renewals").then(r=>setRows(r.data)).catch(()=>{}); }, []);
  const shown = rows.filter(r => filter==="all" || r.status===filter);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="font-display text-2xl text-[color:var(--maroon)]">Renewals due & not renewed</div><p className="text-xs text-neutral-500">Owners get email + in-app reminders at 60/45/30/20/15/10/5…1/0 days, then follow-ups at 1/3/7/15 days after expiry. Tap WhatsApp to send the prewritten message.</p></div>
        <div className="inline-flex p-1 rounded-full bg-[color:var(--ivory-2)]">{[["all","All"],["due","Due soon"],["expired","Not renewed"]].map(([k,l])=><button key={k} onClick={()=>setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter===k ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)]" : "text-[color:var(--maroon)]"}`} data-testid={`renew-filter-${k}`}>{l}</button>)}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {shown.map(r=>(
          <div key={r.venue_id} className={`card-luxury p-4 ${r.status==="expired" ? "!border-rose-300" : r.days_left <= 10 ? "!border-amber-300" : ""}`} data-testid={`renewal-${r.venue_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><div className="font-semibold">{r.venue}</div><div className="text-xs text-neutral-500">{CAT_LABEL[r.category]} · {r.city}</div><div className="text-xs mt-1">{r.owner} · {r.phone || "no phone"} · {r.email}</div></div>
              <span className={`text-[10px] px-2 py-1 rounded-full font-bold whitespace-nowrap ${r.status==="expired" ? "bg-rose-600 text-white" : "bg-[color:var(--gold)] text-[color:var(--maroon-2)]"}`}>{r.status==="expired" ? `Expired ${-r.days_left}d ago` : `${r.days_left}d left`}</span>
            </div>
            <div className="mt-3 flex gap-2">
              {r.wa_link ? <a href={r.wa_link} target="_blank" rel="noreferrer" className="flex-1 !py-2 text-xs rounded-full bg-emerald-600 text-white font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-emerald-700" data-testid={`renewal-wa-${r.venue_id}`}><MessageCircle className="w-3.5 h-3.5"/> WhatsApp owner</a> : <span className="flex-1 text-xs text-neutral-400 text-center py-2">No phone on file</span>}
              <button onClick={()=>{ navigator.clipboard?.writeText(r.message); toast.success("Message copied"); }} className="btn-ghost !py-2 !px-3 text-xs"><Copy className="w-3.5 h-3.5"/></button>
              <a href={`mailto:${r.email}`} className="btn-ghost !py-2 !px-3 text-xs"><Mail className="w-3.5 h-3.5"/></a>
            </div>
          </div>
        ))}
        {!shown.length && <div className="col-span-full text-center py-16 text-neutral-500 border border-dashed border-[color:var(--gold)]/50 rounded-3xl">Nothing due in the next 60 days.</div>}
      </div>
    </div>
  );
}

/* ============ Homepage config ============ */
function HomepageTab() {
  const [items, setItems] = useState([]);
  const [hl, setHl] = useState([]);
  const [add, setAdd] = useState("");
  useEffect(()=>{ api.get("/config/popular-cities").then(r=>setItems(asArray(r.data))).catch(()=>{}); api.get("/config/highlighted-cities").then(r=>setHl(asArray(r.data))).catch(()=>{}); }, []);
  const move = (arr, set, i, d) => { const n=[...arr]; const j=i+d; if (j<0||j>=n.length) return; [n[i],n[j]]=[n[j],n[i]]; set(n); };
  const save = async () => { try { await api.put("/config/popular-cities", items); toast.success("Popular cities saved"); } catch { toast.error("Failed"); } };
  const saveHl = async () => { try { await api.put("/config/highlighted-cities", hl); toast.success("Highlighted order saved"); } catch { toast.error("Failed"); } };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Popular wedding cities (homepage)">
        <p className="text-xs text-neutral-500 -mt-2 mb-3">Rename, reorder, swap the image or tagline. Shown in this exact order.</p>
        <div className="space-y-2">
          {items.map((c, i)=>(
            <div key={i} className="grid grid-cols-[28px_1fr_auto] gap-2 items-start bg-white rounded-xl border border-[color:var(--line)] p-2" data-testid={`popular-row-${i}`}>
              <span className="w-7 h-7 rounded-full bg-[color:var(--maroon)] text-[color:var(--cream-primary)] text-xs font-bold flex items-center justify-center mt-1">{i+1}</span>
              <div className="grid sm:grid-cols-2 gap-1.5">
                <CitySelect value={c.name} onChange={(v)=>{ const n=[...items]; n[i]={...c, name:v}; setItems(n); }} testId={`popular-city-${i}`}/>
                <input value={c.tagline||""} onChange={e=>{ const n=[...items]; n[i]={...c, tagline:e.target.value}; setItems(n); }} placeholder="Tagline" className="input !py-2"/>
                <div className="sm:col-span-2"><ImageField value={c.img||""} folder="cities" onChange={(v)=>setItems((cur)=>{ const n=[...cur]; n[i]={...n[i], img:v}; return n; })} testId={`popular-img-${i}`}/></div>
              </div>
              <div className="flex flex-col gap-1"><button onClick={()=>move(items,setItems,i,-1)} className="p-1 rounded hover:bg-[color:var(--ivory)]"><ArrowUp className="w-4 h-4"/></button><button onClick={()=>move(items,setItems,i,1)} className="p-1 rounded hover:bg-[color:var(--ivory)]"><ArrowDown className="w-4 h-4"/></button><button onClick={()=>setItems(items.filter((_,j)=>j!==i))} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4"/></button></div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4"><button onClick={()=>setItems([...items, { name:"", img:"", tagline:"" }])} className="btn-ghost !py-2 text-sm"><Plus className="w-4 h-4"/> Add city</button><button onClick={save} className="btn-wine !py-2 text-sm" data-testid="save-popular">Save</button></div>
      </Card>
      <Card title="Highlighted page — city order">
        <div className="space-y-1.5">
          {hl.map((c, i)=>(
            <div key={c} className="flex items-center gap-2 bg-white rounded-xl border border-[color:var(--line)] px-3 py-1.5 text-sm">
              <span className="w-6 h-6 rounded-full bg-[color:var(--gold)] text-[color:var(--maroon-2)] text-[11px] font-bold flex items-center justify-center">{i+1}</span>
              <span className="flex-1">{c}</span>
              <button onClick={()=>move(hl,setHl,i,-1)} className="p-1 rounded hover:bg-[color:var(--ivory)]"><ArrowUp className="w-4 h-4"/></button><button onClick={()=>move(hl,setHl,i,1)} className="p-1 rounded hover:bg-[color:var(--ivory)]"><ArrowDown className="w-4 h-4"/></button><button onClick={()=>setHl(hl.filter(x=>x!==c))} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4 items-center"><div className="flex-1"><CitySelect value={add} onChange={setAdd} testId="hl-admin-add" placeholder="Add city"/></div><button onClick={()=>{ if (add && !hl.includes(add)) setHl([...hl, add]); setAdd(""); }} className="btn-ghost !py-2.5 !px-3"><Plus className="w-4 h-4"/></button><button onClick={saveHl} className="btn-wine !py-2.5 text-sm" data-testid="save-hl-order">Save</button></div>
      </Card>
    </div>
  );
}

function NewsletterTab() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/newsletter").then(r=>setRows(r.data)).catch(()=>{}); }, []);
  return (
    <Card title={`Newsletter subscribers (${rows.length})`}>
      <div className="flex justify-end -mt-10 mb-4"><button onClick={()=>{ navigator.clipboard?.writeText(rows.map(r=>r.email).join(", ")); toast.success("Emails copied"); }} className="btn-ghost !py-1.5 !px-3 text-xs"><Copy className="w-3.5 h-3.5"/> Copy all</button></div>
      <div className="divide-y divide-[color:var(--line)] text-sm max-h-[520px] overflow-y-auto">
        {rows.map(r=><div key={r.email} className="py-2 flex justify-between"><span>{r.email}</span><span className="text-xs text-neutral-400">{new Date(r.created_at).toLocaleDateString("en-IN")}</span></div>)}
        {!rows.length && <div className="py-10 text-center text-neutral-500">No subscribers yet.</div>}
      </div>
    </Card>
  );
}

/* ============ Users ============ */
function UsersTab() {
  const [q, setQ] = useState(""), [role, setRole] = useState(""), [cityQ, setCityQ] = useState(""), [users, setUsers] = useState([]);
  useEffect(()=>{ api.get("/users", { params: { q, role, city: cityQ }}).then(r=>setUsers(r.data)); }, [q, role, cityQ]);
  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <input placeholder="Search name / email / phone" value={q} onChange={e=>setQ(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-full border" data-testid="user-search-q"/>
        </div>
        <select value={role} onChange={e=>setRole(e.target.value)} className="px-3 py-2 rounded-full border text-sm">
          <option value="">All roles</option>
          <option value="customer">Customer</option>
          <option value="venue_owner">Venue Owner</option>
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
          <option value="major_admin">Major Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <input placeholder="City" value={cityQ} onChange={e=>setCityQ(e.target.value)} className="px-3 py-2 rounded-full border text-sm"/>
      </div>
      <div className="card-luxury p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest-2 text-neutral-500 text-left border-b border-[color:var(--line)]"><tr><th className="p-2">Name</th><th>Email</th><th>Phone</th><th>City</th><th>Role</th><th>Target</th></tr></thead>
          <tbody>
            {users.map(u=>(<tr key={u.id} className="border-b border-[color:var(--line)]/60"><td className="p-2 font-medium">{u.name}</td><td>{u.email}</td><td>{u.phone||"—"}</td><td>{u.city||"—"}</td><td className="capitalize">{u.role?.replaceAll("_"," ")}</td><td>{u.target||"—"}</td></tr>))}
            {!users.length && <tr><td colSpan={6} className="text-center py-6 text-neutral-500">No users</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Team & Roles ============ */
function TeamTab() {
  const [team, setTeam] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const load = () => api.get("/team/admins").then(r=>setTeam(r.data));
  useEffect(()=>{ load(); }, []);

  const togglePerm = async (u, key) => {
    const perms = new Set(u.permissions || []);
    if (perms.has(key)) perms.delete(key); else perms.add(key);
    const list = Array.from(perms);
    await api.put(`/users/${u.id}/permissions`, list);
    toast.success("Permissions updated");
    load();
  };
  const del = async (u) => { if (!window.confirm(`Remove ${u.name}?`)) return; await api.delete(`/users/${u.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-widest-2 text-neutral-500">Team & Roles</div>
          <div className="text-sm text-neutral-600">Add major admins & admins, then toggle exactly what they can access.</div>
        </div>
        <button onClick={()=>setOpenAdd(true)} className="btn-wine !py-2 !px-4 text-sm" data-testid="add-admin-btn"><Plus className="w-4 h-4"/> Add team member</button>
      </div>

      <div className="space-y-3">
        {team.map(u => (
          <div key={u.id} className="card-luxury p-5" data-testid={`team-row-${u.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium text-neutral-900">{u.name}</div>
                <div className="text-xs text-neutral-500">{u.email} · <span className="capitalize">{u.role.replaceAll("_"," ")}</span></div>
              </div>
              {u.role !== "super_admin" && (
                <button onClick={()=>del(u)} className="text-xs px-3 py-1 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100">Remove</button>
              )}
            </div>
            {u.role !== "super_admin" && (
              <div className="mt-4 grid md:grid-cols-2 gap-2">
                {PERMISSION_KEYS.map(p => {
                  const on = (u.permissions || []).includes(p.k);
                  return (
                    <label key={p.k} className="flex items-center justify-between px-3 py-2 rounded-lg border border-[color:var(--line)] hover:bg-[color:var(--ivory-2)] cursor-pointer transition-colors" data-testid={`perm-${u.id}-${p.k}`}>
                      <span className="text-sm text-neutral-700">{p.l}</span>
                      <input type="checkbox" checked={on} onChange={()=>togglePerm(u, p.k)} className="accent-[color:var(--wine)] w-4 h-4"/>
                    </label>
                  );
                })}
              </div>
            )}
            {u.role === "super_admin" && (
              <div className="mt-3 text-[11px] text-neutral-500 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5"/> Full access — cannot be restricted</div>
            )}
          </div>
        ))}
      </div>

      {openAdd && <AddAdminModal onClose={()=>setOpenAdd(false)} onSaved={()=>{ setOpenAdd(false); load(); }}/>}
    </div>
  );
}

function AddAdminModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name:"", email:"", password:"", role:"admin", permissions: ["crm"] });
  useEscapeKey(onClose);
  const toggle = (k) => setF(prev => ({ ...prev, permissions: prev.permissions.includes(k) ? prev.permissions.filter(x=>x!==k) : [...prev.permissions, k] }));
  const save = async (e) => {
    e.preventDefault();
    const bad = !f.name.trim() ? "Enter a name" : emailError(f.email, { required: true }) || (!f.password ? "Choose a password" : "");
    if (bad) { toast.error(bad); return; }
    try { await api.post("/users/admin", f); toast.success("Team member added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form noValidate onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-3 max-h-[92vh] overflow-y-auto" data-testid="add-admin-modal">
        <h2 className="font-display text-2xl">Add team member</h2>
        <div className="grid grid-cols-2 gap-2">
          <input required placeholder="Full name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="input col-span-2" data-testid="a-name"/>
          <input required type="email" inputMode="email" placeholder="Email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} className="input col-span-2" data-testid="a-email"/>
          <input required type="text" autoComplete="new-password" placeholder="Password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="input col-span-2" data-testid="a-password"/>
          <select value={f.role} onChange={e=>setF({...f,role:e.target.value})} className="input col-span-2">
            <option value="major_admin">Major Admin</option>
            <option value="admin">Admin (Salesperson)</option>
          </select>
        </div>
        <div className="text-xs uppercase tracking-widest-2 text-neutral-500 pt-2">Permissions</div>
        <div className="grid grid-cols-1 gap-1">
          {PERMISSION_KEYS.map(p => (
            <label key={p.k} className="flex items-center justify-between px-3 py-2 rounded-lg border border-[color:var(--line)] cursor-pointer">
              <span className="text-sm">{p.l}</span>
              <input type="checkbox" checked={f.permissions.includes(p.k)} onChange={()=>toggle(p.k)} className="accent-[color:var(--wine)] w-4 h-4" data-testid={`a-perm-${p.k}`}/>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button className="btn-wine" data-testid="a-save">Create</button>
        </div>
      </form>
    </div>
  );
}

/* ============ CMS ============ */
function CMSTab({ onChange }) {
  const [sub, setSub] = useState("blogs");
  return (
    <div>
      <div className="inline-flex flex-wrap bg-white border border-[color:var(--line)] rounded-full p-1 mb-5">
        {[["blogs","Blog stories", FileText],["testimonials","Testimonials", Star],["messages","Contact messages", Inbox],["site","Site settings", Globe]].map(([k,l,I])=>(
          <button key={k} onClick={()=>setSub(k)} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] font-semibold ${sub===k ? "bg-[color:var(--maroon)] text-white" : "text-[color:var(--maroon)]"}`} data-testid={`cms-sub-${k}`}><I className="w-4 h-4"/> {l}</button>
        ))}
      </div>
      {sub==="blogs" && <BlogsCMS/>}
      {sub==="testimonials" && <TestimonialsCMS/>}
      {sub==="messages" && <MessagesCMS onChange={onChange}/>}
      {sub==="site" && <SiteSettingsCMS/>}
    </div>
  );
}

function MessagesCMS({ onChange }) {
  const [rows, setRows] = useState(null);
  const tick = useTick();
  const load = () => api.get("/contact-messages").then(r=>setRows(asArray(r.data))).catch(()=>setRows([]));
  useEffect(()=>{ load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const read = async (m) => { if (m.read) return; await api.post(`/contact-messages/${m.id}/read`); load(); onChange?.(); };
  const del = async (m) => { if (!window.confirm("Delete this message?")) return; await api.delete(`/contact-messages/${m.id}`); load(); onChange?.(); };
  const intent = { plan: "Planning an event", list: "Wants to list a business", other: "Something else" };
  if (rows === null) return <div className="h-40 skeleton"/>;
  return (
    <div className="space-y-3" data-testid="cms-messages">
      {rows.map((m)=>(
        <div key={m.id} onClick={()=>read(m)} className={`rounded-[18px] bg-white border p-4 md:p-5 ${m.read ? "border-[color:var(--line)]" : "border-[color:var(--maroon)]/40 shadow-[0_6px_20px_rgba(140,28,34,.08)]"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-[16px] text-[color:var(--ink)] flex items-center gap-2">{!m.read && <span className="w-2 h-2 rounded-full bg-[color:var(--maroon)]"/>}{m.name} <span className="text-[13px] font-normal text-neutral-500">· {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span></div>
              <div className="text-[13px] text-[color:var(--ink-2)] mt-0.5">{intent[m.intent] || m.intent}{m.city ? ` · ${m.city}` : ""}{m.event_type ? ` · ${m.event_type}` : ""}{m.event_date ? ` · ${m.event_date}` : ""}{m.pax ? ` · ${m.pax} guests` : ""}{m.budget ? ` · ${m.budget}` : ""}</div>
              {m.message && <p className="text-[15px] text-[color:var(--ink)] mt-2 whitespace-pre-line">{m.message}</p>}
            </div>
            <div className="flex gap-2 shrink-0" onClick={(e)=>e.stopPropagation()}>
              <a href={`tel:${m.phone}`} className="btn-ghost !py-2 !px-3 !text-[13px]"><Phone className="w-3.5 h-3.5"/> {m.phone}</a>
              <a href={`https://wa.me/${(m.phone||"").replace(/\D/g,"").replace(/^(\d{10})$/,"91$1")}`} target="_blank" rel="noreferrer" className="!py-2 !px-3 text-[13px] rounded-full bg-emerald-600 text-white font-semibold inline-flex items-center gap-1.5 press"><MessageCircle className="w-3.5 h-3.5"/> WhatsApp</a>
              {m.email && <a href={`mailto:${m.email}`} className="btn-ghost !py-2 !px-3"><Mail className="w-3.5 h-3.5"/></a>}
              <button onClick={()=>del(m)} className="!py-2 !px-3 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white press"><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
          </div>
        </div>
      ))}
      {!rows.length && <div className="text-center py-16 text-neutral-500 border-2 border-dashed border-[color:var(--line)] rounded-3xl">No messages yet. Messages from the Contact page appear here and in CRM → Leads.</div>}
    </div>
  );
}

function SiteSettingsCMS() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ api.get("/config/site").then(r=>setF(r.data || {})).catch(()=>setF({})); }, []);
  const save = async (e) => {
    e.preventDefault();
    const bad = phoneError(f.phone) || (f.whatsapp && phoneError(f.whatsapp) ? "Enter a valid WhatsApp number" : "") || emailError(f.email);
    if (bad) { toast.error(bad); return; }
    setBusy(true); try { await api.put("/config/site", f); toast.success("Site settings saved"); } catch (er) { toast.error(formatApiErrorDetail(er?.response?.data?.detail)); } finally { setBusy(false); } };
  if (!f) return <div className="h-40 skeleton"/>;
  const F = (k, l, ph, type = "text") => <label className="block"><span className="label">{l}</span><input type={type} value={f[k] || ""} onChange={(e)=>setF({ ...f, [k]: e.target.value })} placeholder={ph} className="input" data-testid={`site-${k}`}/></label>;
  return (
    <form noValidate onSubmit={save} className="grid lg:grid-cols-2 gap-6" data-testid="cms-site">
      <Panel title="Contact details">
        <div className="grid sm:grid-cols-2 gap-4">
          {F("phone", "Phone (shown on the site)", "+91 98000 00000")}
          {F("whatsapp", "WhatsApp number (with country code)", "919800000000")}
          {F("email", "Email", "hello@dartmyvenue.com", "email")}
          {F("hours", "Hours", "9 AM – 9 PM, every day")}
          <div className="sm:col-span-2">{F("address", "Address", "Belgaum, Karnataka, India")}</div>
        </div>
      </Panel>
      <Panel title="Video & social links">
        <div className="space-y-4">
          {F("intro_video_url", "“What is DartMyVenue” video — YouTube Shorts link (Contact page)", "https://youtube.com/shorts/…")}
          {f.intro_video_url && <div className="text-[13px] text-[color:var(--ink-2)] inline-flex items-center gap-1.5"><YoutubeIcon className="w-4 h-4 text-rose-600"/> Shown in the phone frame on the Contact page.</div>}
          {F("instagram", "Instagram", "https://instagram.com/…")}
          {F("facebook", "Facebook", "https://facebook.com/…")}
          {F("youtube", "YouTube channel", "https://youtube.com/@…")}
        </div>
      </Panel>
      <div className="lg:col-span-2"><button disabled={busy} className="btn-wine" data-testid="site-save">{busy ? "Saving…" : "Save site settings"}</button></div>
    </form>
  );
}

function BlogsCMS() {
  const [blogs, setBlogs] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/blogs", { params: { limit: 100 } }).then(r=>setBlogs(asArray(r.data?.items))).catch(()=>setBlogs([]));
  useEffect(()=>{ load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete blog?")) return; await api.delete(`/blogs/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><button onClick={()=>setOpenAdd(true)} className="btn-wine !py-2 !px-4 text-sm" data-testid="cms-add-blog"><Plus className="w-4 h-4"/> New blog</button></div>
      <div className="grid md:grid-cols-3 gap-4">
        {blogs.map(b=>(
          <div key={b.id} className="card-luxury overflow-hidden">
            {b.cover_image && <img src={mediaUrl(b.cover_image)} className="aspect-video w-full object-cover"/>}
            <div className="p-4">
              <div className="font-display text-lg">{b.title}</div>
              <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{b.excerpt}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={()=>setEdit(b)} className="text-xs px-3 py-1 rounded-full bg-neutral-100">Edit</button>
                <button onClick={()=>del(b.id)} className="text-xs px-3 py-1 rounded-full bg-rose-50 text-rose-700">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {!blogs.length && <div className="col-span-3 text-center py-16 text-neutral-500 border border-dashed rounded-2xl">No blogs yet. Create your first post.</div>}
      </div>
      {(openAdd || edit) && <BlogEditor initial={edit} onClose={()=>{ setOpenAdd(false); setEdit(null); }} onSaved={()=>{ setOpenAdd(false); setEdit(null); load(); }}/>}
    </div>
  );
}

function BlogEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...initial, images: asArray(initial.images), tags: (initial.tags||[]).join(", "), sub_tags: (initial.sub_tags||[]).join(", "), sections: initial.sections || [] } : { title:"", slug:"", excerpt:"", content:"", cover_image:"", images:[], tags:"", sub_tags:"", author:"Team DartMyVenue", read_minutes:4, sections:[] });
  const split = (s) => typeof s === "string" ? s.split(",").map(t=>t.trim()).filter(Boolean) : (s||[]);
  const setSec = (i, k, v) => setF((cur) => { const n=[...cur.sections]; n[i]={...n[i], [k]:v}; return {...cur, sections:n}; });
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...f, images: asArray(f.images).filter(Boolean), tags: split(f.tags), sub_tags: split(f.sub_tags), read_minutes: Number(f.read_minutes)||4, sections: f.sections.map(s=>({ heading: s.heading||null, body: s.body||null, image: s.image||null, caption: s.caption||null })) };
    delete payload.id; delete payload.created_at;
    try { if (initial?.id) await api.put(`/blogs/${initial.id}`, payload); else await api.post("/blogs", payload); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const autoSlug = () => setF({...f, slug: f.title.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"")});
  return (
    <div className="fixed inset-0 z-[60] bg-[color:var(--maroon-2)]/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-[color:var(--cream)] rounded-t-3xl md:rounded-3xl w-full max-w-3xl max-h-[94vh] overflow-y-auto border border-[color:var(--gold)]" data-testid="blog-editor">
        <div className="sticky top-0 bg-[color:var(--cream)]/95 backdrop-blur border-b border-[color:var(--line)] px-6 py-4 flex items-center justify-between z-10"><h2 className="font-display text-2xl text-[color:var(--maroon)]">{initial ? "Edit story" : "New story"}</h2><button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[color:var(--ivory)]"><X className="w-5 h-5"/></button></div>
        <div className="p-6 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block md:col-span-2"><span className="label">Title</span><input required value={f.title} onChange={e=>setF({...f,title:e.target.value})} onBlur={()=>!f.slug && autoSlug()} className="input" data-testid="blog-title"/></label>
            <label className="block"><span className="label">Slug (URL)</span><input required value={f.slug} onChange={e=>setF({...f,slug:e.target.value})} className="input" data-testid="blog-slug"/></label>
            <label className="block"><span className="label">Author</span><input value={f.author||""} onChange={e=>setF({...f,author:e.target.value})} className="input"/></label>
            <label className="block"><span className="label">Read time (minutes)</span><input type="number" min="1" value={f.read_minutes} onChange={e=>setF({...f,read_minutes:e.target.value})} className="input"/></label>
            <div className="md:col-span-2"><ImageField label="Cover image" folder="blogs" value={f.cover_image || ""} onChange={(v)=>setF((cur)=>({...cur, cover_image:v}))} testId="blog-cover"/></div>
            <div className="md:col-span-2"><MediaUploader label="Gallery images" kind="image" folder="blogs" value={f.images} onChange={(v)=>setF((cur)=>({...cur, images:v}))} testId="blog-gallery"/></div>
            <div className="block"><label className="block"><span className="label">Tags (comma separated)</span><input value={f.tags} onChange={e=>setF({...f,tags:e.target.value})} className="input" placeholder="Venues, Planning"/></label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {BLOG_TOPICS.map((t)=>{ const cur = String(f.tags||"").split(",").map((x)=>x.trim()).filter(Boolean); const on = cur.includes(t); return (
                  <button type="button" key={t} onClick={()=>setF({ ...f, tags: (on ? cur.filter((x)=>x!==t) : [...cur, t]).join(", ") })} className={`text-[12px] px-2.5 py-1 rounded-full border ${on ? "bg-[color:var(--maroon)] text-white border-[color:var(--maroon)]" : "bg-white border-[color:var(--line)] text-[color:var(--maroon)]"}`}>{t}</button>
                ); })}
              </div>
            </div>
            <label className="block"><span className="label">Sub-tags / hashtags</span><input value={f.sub_tags} onChange={e=>setF({...f,sub_tags:e.target.value})} className="input" placeholder="lawns, monsoon"/></label>
          </div>
          <label className="block"><span className="label">Excerpt</span><textarea required rows={2} value={f.excerpt} onChange={e=>setF({...f,excerpt:e.target.value})} className="input"/></label>
          <label className="block"><span className="label">Intro content</span><textarea required rows={5} value={f.content} onChange={e=>setF({...f,content:e.target.value})} className="input"/></label>
          <div>
            <div className="flex items-center justify-between mb-2"><span className="label !mb-0">Sections (sub-heading + body + image)</span><button type="button" onClick={()=>setF({...f, sections:[...f.sections, { heading:"", body:"", image:"", caption:"" }]})} className="text-xs inline-flex items-center gap-1 text-[color:var(--maroon)] font-semibold" data-testid="blog-add-section"><Plus className="w-3.5 h-3.5"/> Add section</button></div>
            <div className="space-y-3">
              {f.sections.map((s, i)=>(
                <div key={i} className="rounded-2xl border border-[color:var(--line)] bg-white p-3 space-y-2" data-testid={`blog-section-${i}`}>
                  <div className="flex gap-2"><input placeholder="Sub-heading" value={s.heading||""} onChange={e=>setSec(i,"heading",e.target.value)} className="input !py-2 flex-1"/><button type="button" onClick={()=>setF({...f, sections:f.sections.filter((_,j)=>j!==i)})} className="p-2 text-rose-600"><Trash2 className="w-4 h-4"/></button></div>
                  <textarea rows={3} placeholder="Body" value={s.body||""} onChange={e=>setSec(i,"body",e.target.value)} className="input !py-2"/>
                  <ImageField folder="blogs" value={s.image||""} onChange={(v)=>setSec(i,"image",v)} testId={`blog-section-img-${i}`}/>
                  <input placeholder="Image caption" value={s.caption||""} onChange={e=>setSec(i,"caption",e.target.value)} className="input !py-2 text-xs"/>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-[color:var(--cream)]/95 backdrop-blur border-t border-[color:var(--line)] px-6 py-4 flex justify-end gap-2"><button type="button" onClick={onClose} className="btn-ghost !py-2.5">Cancel</button><button className="btn-wine !py-2.5" data-testid="blog-save">Save story</button></div>
      </form>
    </div>
  );
}

function TestimonialsCMS() {
  const [tms, setTms] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const load = () => api.get("/testimonials").then(r=>setTms(asArray(r.data))).catch(()=>{});
  useEffect(()=>{ load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/testimonials/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><button onClick={()=>setOpenAdd(true)} className="btn-wine !py-2 !px-4 text-sm" data-testid="cms-add-testimonial"><Plus className="w-4 h-4"/> Add testimonial</button></div>
      <div className="grid md:grid-cols-3 gap-4">
        {tms.map(t=>(
          <div key={t.id} className="card-luxury p-5">
            <div className="flex gap-1 text-[color:var(--gold)] mb-2">{Array.from({length: t.rating||5}).map((_,i)=><Star key={i} className="w-3.5 h-3.5 fill-current"/>)}</div>
            <div className="font-display text-lg leading-tight">"{t.text}"</div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm"><div className="font-medium">{t.name}</div><div className="text-xs text-neutral-500">{t.city}</div></div>
              <button onClick={()=>del(t.id)} className="text-xs px-3 py-1 rounded-full bg-rose-50 text-rose-700">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {openAdd && <TestimonialEditor onClose={()=>setOpenAdd(false)} onSaved={()=>{ setOpenAdd(false); load(); }}/>}
    </div>
  );
}

function TestimonialEditor({ onClose, onSaved }) {
  const [f, setF] = useState({ name:"", text:"", city:"", rating:5, avatar:"" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/testimonials", f); toast.success("Added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3" data-testid="testimonial-editor">
        <h2 className="font-display text-2xl">New testimonial</h2>
        <input required placeholder="Couple / Name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="w-full px-3 py-2.5 rounded-lg border"/>
        <input placeholder="City" value={f.city} onChange={e=>setF({...f,city:e.target.value})} className="w-full px-3 py-2.5 rounded-lg border"/>
        <ImageField label="Photo" folder="testimonials" value={f.avatar} onChange={(v)=>setF((cur)=>({...cur, avatar:v}))} testId="testimonial-avatar"/>
        <textarea required rows={4} placeholder="Testimonial" value={f.text} onChange={e=>setF({...f,text:e.target.value})} className="w-full px-3 py-2.5 rounded-lg border"/>
        <input type="number" min="1" max="5" value={f.rating} onChange={e=>setF({...f,rating:Number(e.target.value)})} className="w-full px-3 py-2.5 rounded-lg border"/>
        <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="btn-ghost">Cancel</button><button className="btn-wine">Save</button></div>
      </form>
    </div>
  );
}

/* ============ Cities & Pricing ============ */
function CitiesTab({ cities, onReload }) {
  const [f, setF] = useState({ name:"", state:"" });
  const [q, setQ] = useState("");
  const add = async (e) => { e.preventDefault(); try { await api.post("/cities", { ...f, is_active: true }); toast.success("City added"); setF({name:"",state:""}); onReload(); } catch (er) { toast.error(formatApiErrorDetail(er?.response?.data?.detail) || "Failed"); } };
  const shown = useMemo(() => { const t = q.trim().toLowerCase(); return (t ? cities.filter((c) => c.name.toLowerCase().includes(t) || (c.state||"").toLowerCase().includes(t)) : cities).slice(0, 300); }, [cities, q]);
  const states = useMemo(() => new Set(cities.map((c) => c.state)).size, [cities]);
  return (
    <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
      <Panel title="Add a city">
        <form onSubmit={add} className="space-y-3">
          <input required placeholder="City" value={f.name} onChange={e=>setF({...f, name:e.target.value})} className="input"/>
          <input required placeholder="State" value={f.state} onChange={e=>setF({...f, state:e.target.value})} className="input" list="state-list"/>
          <datalist id="state-list">{Array.from(new Set(cities.map((c)=>c.state))).sort().map((st)=><option key={st} value={st}/>)}</datalist>
          <button className="btn-wine w-full">Add city</button>
        </form>
      </Panel>
      <Panel title={`${cities.length.toLocaleString("en-IN")} cities in ${states} states`}>
        <div className="relative mb-3"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/><input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search a city or state" className="input !pl-9"/></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 text-[14px] max-h-[520px] overflow-y-auto">
          {shown.map(c=><div key={c.name + c.state} className="px-2 py-1.5 border-b border-[color:var(--line)]/50 flex items-center justify-between gap-2 group"><span className="truncate">{c.name} <span className="text-[12px] text-neutral-400">{c.state}</span></span><button onClick={async()=>{ if(!window.confirm(`Remove ${c.name}, ${c.state}?`)) return; await api.delete(`/cities/${encodeURIComponent(c.name)}`, { params: { state: c.state } }); onReload(); }} className="opacity-0 group-hover:opacity-100 text-rose-600 p-1" title="Remove"><Trash2 className="w-3.5 h-3.5"/></button></div>)}
        </div>
        {!q && cities.length > 300 && <div className="text-[13px] text-neutral-500 mt-2">Showing the first 300 — search to find any city.</div>}
      </Panel>
    </div>
  );
}

function PricingTab({ pricing, setPricing }) {
  const save = async () => { try { await api.put("/config/pricing", pricing); toast.success("Pricing saved"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Only the super admin can change pricing"); } };
  const cats = pricing?.categories || {};
  const keys = [...CATEGORIES.map((c) => c.key).filter((k) => cats[k]), ...Object.keys(cats).filter((k) => !CATEGORIES.find((c) => c.key === k))];
  const setCat = (cat, k, v) => setPricing({ ...pricing, categories: { ...cats, [cat]: { ...cats[cat], [k]: Number(v) } } });
  return (
    <Panel title="Plan prices (₹, GST inclusive)">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {keys.map((cat)=>(
          <div key={cat} className="p-4 rounded-2xl bg-[color:var(--cream)] border border-[color:var(--line)]">
            <div className="font-semibold mb-2">{catLabel(cat)}</div>
            <div className="flex gap-2">
              {["6m","12m"].map(k=>(
                <label key={k} className="flex-1"><span className="text-[12px] text-neutral-500">{k === "6m" ? "6 months" : "12 months"}</span>
                  <input type="number" value={cats[cat]?.[k] ?? 0} onChange={e=>setCat(cat, k, e.target.value)} className="input !py-2 font-num"/></label>
              ))}
            </div>
          </div>
        ))}
        <div className="p-4 rounded-2xl bg-[color:var(--ivory)] border border-[color:var(--gold)]/40">
          <div className="font-semibold mb-2">Premium spotlight (per listing)</div>
          <div className="flex gap-2">
            {["6m","12m"].map(k=>(
              <label key={k} className="flex-1"><span className="text-[12px] text-neutral-500">{k === "6m" ? "6 months" : "12 months"}</span>
                <input type="number" value={pricing?.premium_slot?.[k] ?? 0} onChange={e=>setPricing({ ...pricing, premium_slot: { ...(pricing.premium_slot || {}), [k]: Number(e.target.value) } })} className="input !py-2 font-num"/></label>
            ))}
          </div>
        </div>
      </div>
      <button onClick={save} className="btn-wine mt-5" data-testid="save-pricing">Save pricing</button>
    </Panel>
  );
}

/* ============ Reminders ============ */
function RemindersTab() {
  const [email, setEmail] = useState(""); const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/admin/renewal-reminders/test`, null, { params: { email }}); toast.success("Preview email sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="card-luxury p-6">
        <div className="font-display text-2xl mb-1">Renewal reminder emails</div>
        <p className="text-sm text-neutral-600 mb-4">Owners auto-receive branded emails + in-app notifications at <strong>60, 45, 30, 20, 15, 10, 5, 4, 3, 2, 1 & 0</strong> days before expiry, and <strong>1, 3, 7, 15</strong> days after if not renewed. Runs daily at <strong>9:00 IST</strong>. WhatsApp follow-ups live in the Renewals tab.</p>
        <ul className="text-sm space-y-2 text-neutral-700">
          <li>· De-duplicated per owner + venue + milestone</li>
          <li>· From: <strong>DartMyVenue</strong> · Reply-to: hello@dartmyvenue.com</li>
        </ul>
      </div>
      <form onSubmit={send} className="card-luxury p-6 space-y-3" data-testid="reminder-test-form">
        <div className="font-display text-2xl">Send a preview</div>
        <input required type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border" data-testid="reminder-test-email"/>
        <button disabled={busy} className="btn-wine w-full justify-center" data-testid="reminder-test-send">{busy ? "Sending…" : "Send preview email"}</button>
        <div className="text-[12px] text-neutral-500">Needs SMTP settings in backend/.env (SMTP_HOST, SMTP_USER, SMTP_PASSWORD).</div>
      </form>
    </div>
  );
}

/* ============ Shared ============ */
function Stat({icon: Icon, label, value}) {
  return (
    <div className="card-luxury p-5 relative overflow-hidden group">
      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-[color:var(--wine)]/5 group-hover:bg-[color:var(--wine)]/10 transition-colors"/>
      <Icon className="w-5 h-5 text-[color:var(--wine)] mb-3 relative"/>
      <div className="text-3xl font-display relative">{value ?? "—"}</div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-widest-2 mt-1 relative">{label}</div>
    </div>
  );
}

function Card({ title, children }) {
  return <Panel title={title}>{children}</Panel>;
}
